import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { db } from '@/lib/firebase';
import { collection, addDoc, query, where, getDocs, orderBy, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useToast } from '@/components/ui/use-toast';
import { Patient, PatientSession, PatientPayment } from '@/types';
import { Plus, CreditCard, Calendar, FileText } from 'lucide-react';

export default function PatientProfile() {
  const { patientId } = useParams();
  const { toast } = useToast();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [sessions, setSessions] = useState<PatientSession[]>([]);
  const [payments, setPayments] = useState<PatientPayment[]>([]);
  const [isAddingSession, setIsAddingSession] = useState(false);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [newSession, setNewSession] = useState<Partial<PatientSession>>({
    procedure: '',
    price: 0,
    notes: ''
  });
  const [newPayment, setNewPayment] = useState<Partial<PatientPayment>>({
    amount: 0,
    paymentMethod: 'cash',
    notes: ''
  });

  useEffect(() => {
    if (patientId) {
      fetchPatientData();
    }
  }, [patientId]);

  const fetchPatientData = async () => {
    try {
      // Fetch patient data
      const patientRef = doc(db, 'patients', patientId!);
      const patientDoc = await getDoc(patientRef);
      if (patientDoc.exists()) {
        setPatient({ id: patientDoc.id, ...patientDoc.data() } as Patient);
      }

      // Fetch sessions
      const sessionsRef = collection(db, 'patientSessions');
      const sessionsQuery = query(
        sessionsRef,
        where('patientId', '==', patientId),
        orderBy('date', 'desc')
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);
      const sessionsData = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PatientSession[];
      setSessions(sessionsData);

      // Fetch payments
      const paymentsRef = collection(db, 'patientPayments');
      const paymentsQuery = query(
        paymentsRef,
        where('patientId', '==', patientId),
        orderBy('date', 'desc')
      );
      const paymentsSnapshot = await getDocs(paymentsQuery);
      const paymentsData = paymentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PatientPayment[];
      setPayments(paymentsData);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء جلب بيانات المريض"
      });
    }
  };

  const handleAddSession = async () => {
    if (!newSession.procedure || !newSession.price) {
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "يرجى ملء جميع الحقول المطلوبة"
      });
      return;
    }

    try {
      const sessionsRef = collection(db, 'patientSessions');
      await addDoc(sessionsRef, {
        ...newSession,
        patientId,
        date: new Date(),
        createdAt: new Date()
      });

      setNewSession({
        procedure: '',
        price: 0,
        notes: ''
      });
      setIsAddingSession(false);
      toast({
        title: "تمت الإضافة",
        description: "تم إضافة الجلسة بنجاح"
      });
      fetchPatientData();
    } catch (error) {
      console.error('Error adding session:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء إضافة الجلسة"
      });
    }
  };

  const handleAddPayment = async () => {
    if (!newPayment.amount) {
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "يرجى إدخال قيمة الدفع"
      });
      return;
    }

    try {
      const paymentsRef = collection(db, 'patientPayments');
      await addDoc(paymentsRef, {
        ...newPayment,
        patientId,
        date: new Date(),
        createdAt: new Date()
      });

      // Update patient's remaining amount
      if (patient) {
        const patientRef = doc(db, 'patients', patientId!);
        await updateDoc(patientRef, {
          remainingAmount: patient.remainingAmount - Number(newPayment.amount),
          updatedAt: new Date()
        });
      }

      setNewPayment({
        amount: 0,
        paymentMethod: 'cash',
        notes: ''
      });
      setIsAddingPayment(false);
      toast({
        title: "تمت الإضافة",
        description: "تم إضافة الدفعة بنجاح"
      });
      fetchPatientData();
    } catch (error) {
      console.error('Error adding payment:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء إضافة الدفعة"
      });
    }
  };

  if (!patient) {
    return <div>جاري التحميل...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-green-600 bg-clip-text text-transparent">
            ملف المريض
          </h1>
        </div>

        {/* Patient Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>معلومات المريض</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>الاسم</Label>
                <p className="text-lg font-semibold">{patient.name}</p>
              </div>
              <div>
                <Label>رقم الهاتف</Label>
                <p className="text-lg font-semibold">{patient.phone}</p>
              </div>
              <div>
                <Label>نوع الحالة</Label>
                <p className="text-lg font-semibold">{patient.caseType}</p>
              </div>
              <div>
                <Label>المبلغ المتبقي</Label>
                <p className="text-lg font-semibold">{patient.remainingAmount} جنيه</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sessions and Payments */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sessions */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>سجل الجلسات</CardTitle>
              <Dialog open={isAddingSession} onOpenChange={setIsAddingSession}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    إضافة جلسة
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>إضافة جلسة جديدة</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label>الإجراء</Label>
                      <Input
                        value={newSession.procedure || ''}
                        onChange={(e) => setNewSession({ ...newSession, procedure: e.target.value })}
                        placeholder="أدخل الإجراء"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>السعر</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newSession.price || ''}
                        onChange={(e) => setNewSession({ 
                          ...newSession, 
                          price: e.target.value ? Number(e.target.value) : 0 
                        })}
                        placeholder="أدخل السعر"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>ملاحظات</Label>
                      <Textarea
                        value={newSession.notes || ''}
                        onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                        placeholder="أدخل أي ملاحظات إضافية"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-4">
                    <Button variant="outline" onClick={() => setIsAddingSession(false)}>
                      إلغاء
                    </Button>
                    <Button onClick={handleAddSession}>
                      إضافة الجلسة
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sessions.map((session) => (
                  <Card key={session.id}>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{session.procedure}</p>
                          <p className="text-sm text-muted-foreground">
                            {format(session.date, 'dd MMMM yyyy', { locale: ar })}
                          </p>
                        </div>
                        <p className="font-semibold">{session.price} جنيه</p>
                      </div>
                      {session.notes && (
                        <p className="text-sm text-muted-foreground mt-2">{session.notes}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Payments */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>سجل الدفعات</CardTitle>
              <Dialog open={isAddingPayment} onOpenChange={setIsAddingPayment}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    إضافة دفعة
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>إضافة دفعة جديدة</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label>قيمة الدفعة</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newPayment.amount || ''}
                        onChange={(e) => setNewPayment({ 
                          ...newPayment, 
                          amount: e.target.value ? Number(e.target.value) : 0 
                        })}
                        placeholder="أدخل قيمة الدفعة"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>طريقة الدفع</Label>
                      <Select
                        value={newPayment.paymentMethod}
                        onValueChange={(value: 'cash' | 'card' | 'transfer') =>
                          setNewPayment({ ...newPayment, paymentMethod: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="اختر طريقة الدفع" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">نقدي</SelectItem>
                          <SelectItem value="card">بطاقة</SelectItem>
                          <SelectItem value="transfer">تحويل</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label>ملاحظات</Label>
                      <Textarea
                        value={newPayment.notes || ''}
                        onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                        placeholder="أدخل أي ملاحظات إضافية"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-4">
                    <Button variant="outline" onClick={() => setIsAddingPayment(false)}>
                      إلغاء
                    </Button>
                    <Button onClick={handleAddPayment}>
                      إضافة الدفعة
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {payments.map((payment) => (
                  <Card key={payment.id}>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">
                            {payment.paymentMethod === 'cash' ? 'نقدي' :
                             payment.paymentMethod === 'card' ? 'بطاقة' : 'تحويل'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {format(payment.date, 'dd MMMM yyyy', { locale: ar })}
                          </p>
                        </div>
                        <p className="font-semibold">{payment.amount} جنيه</p>
                      </div>
                      {payment.notes && (
                        <p className="text-sm text-muted-foreground mt-2">{payment.notes}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 